<div
  class="user-invite-container flex flex-col w-full text-gray-900 dark:text-white rounded-xl overflow-hidden"
  (click)="onModalClick($event)"
>
  <div class="user-invite-body flex flex-col lg:flex-row w-full flex-1">
    <!-- Left Section: Search and User Selection List -->
    <div
      class="left-panel flex-1 flex flex-col p-4 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-[#1E2638]"
    >
      <!-- Search Bar and Status Filter -->
      <div class="flex items-center gap-2.5 mb-3">
        <div class="relative flex-1">
          <span
            class="search-icon-wrapper pointer-events-none text-gray-400 dark:text-gray-400"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <input
            type="text"
            #searchInput
            (input)="onKeyUpSearch(searchInput.value)"
            placeholder="Search users by name or email..."
            class="search-input-box"
          />
          <button
            *ngIf="searchInput.value"
            type="button"
            class="search-clear-custom"
            (click)="clearSearch(searchInput)"
          >
            &times;
          </button>
        </div>
      </div>

      <!-- Select All & Users count toolbar -->
      <div
        class="flex items-center justify-between mb-2.5 px-1 py-1 text-xs select-all-bar"
      >
        <div class="flex items-center gap-2">
          <!-- Master checkbox for current page -->
          <input
            type="checkbox"
            id="selectAllCurrentPage"
            [checked]="isCurrentPageAllSelected()"
            [indeterminate]="isCurrentPageIndeterminate()"
            (change)="toggleSelectAllCurrentPage($event)"
            class="custom-checkbox cursor-pointer"
          />
          <label
            for="selectAllCurrentPage"
            class="cursor-pointer font-medium text-gray-700 dark:text-gray-300 select-none hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
          >
            Select all on current page
          </label>
        </div>

        <div class="flex items-center gap-2 text-xs">
          <!-- Button to select all across all pages -->
          <button
            *ngIf="totalRecords > 0 && !selectAllAcrossPages"
            type="button"
            (click)="selectAllUsersAcrossAllPages()"
            class="text-blue-500 hover:text-blue-400 font-medium hover:underline cursor-pointer bg-transparent border-0 p-0 text-xs transition-colors"
          >
            Select all {{ totalRecords }} users
          </button>
          <button
            *ngIf="selectAllAcrossPages"
            type="button"
            (click)="clearSelection()"
            class="text-amber-500 hover:text-amber-400 font-medium hover:underline cursor-pointer bg-transparent border-0 p-0 text-xs transition-colors"
          >
            Deselect all {{ totalRecords }}
          </button>

          <span
            class="text-gray-800 font-normal border-l border-gray-300 dark:border-gray-700 pl-2"
          >
            {{ totalRecords || users.length }} users found
          </span>
        </div>
      </div>

      <!-- Banner if current page is selected and more pages exist -->
      <div
        *ngIf="
          isCurrentPageAllSelected() &&
          !selectAllAcrossPages &&
          totalRecords > filteredUsers.length
        "
        class="mb-3 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-400 flex items-center justify-between"
      >
        <span>
          All {{ filteredUsers.length }} users on this page are selected.
        </span>
        <button
          type="button"
          (click)="selectAllUsersAcrossAllPages()"
          class="font-semibold text-blue-400 hover:text-blue-300 underline ml-2 cursor-pointer bg-transparent border-0 p-0"
        >
          Select all {{ totalRecords }} users
        </button>
      </div>

      <!-- Banner if all pages are selected -->
      <div
        *ngIf="selectAllAcrossPages"
        class="mb-3 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400 flex items-center justify-between"
      >
        <span>
          ✓ All {{ totalRecords }} users across all pages are selected.
        </span>
        <button
          type="button"
          (click)="clearSelection()"
          class="font-semibold text-emerald-400 hover:text-emerald-300 underline ml-2 cursor-pointer bg-transparent border-0 p-0"
        >
          Clear selection
        </button>
      </div>

      <div
        class="user-list flex-1 pr-1 space-y-2.5 custom-scrollbar"
        style="
          flex: 1 1 0%;
          min-height: 0;
          overflow-y: auto;
        "
        (scroll)="onScroll($event)"
      >
        <!-- Card Skeleton Loader when Searching or Loading Initial Users -->
        <ng-container *ngIf="isSearching">
          <div
            *ngFor="let s of [1, 2, 3, 4, 5]"
            class="user-skeleton-row flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-[#1a253a] bg-gray-50 dark:bg-[#0c1322] mb-2.5"
          >
            <div class="flex items-center gap-3 min-w-0 flex-1 mr-2">
              <!-- Checkbox skeleton -->
              <div class="w-4 h-4 rounded sk-box flex-shrink-0"></div>
              <!-- Avatar skeleton -->
              <div class="w-9 h-9 rounded-full sk-box flex-shrink-0"></div>
              <!-- Name & Email skeleton -->
              <div class="flex flex-col gap-1.5 flex-1 min-w-0">
                <div class="h-3.5 w-32 sk-box rounded"></div>
                <div class="h-2.5 w-48 sk-box rounded"></div>
              </div>
            </div>
            <!-- Right side skeleton -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <div class="w-16 h-5 rounded-full sk-box"></div>
              <div class="w-24 h-7 rounded-lg sk-box"></div>
            </div>
          </div>
        </ng-container>

        <!-- Actual User Rows -->
        <ng-container *ngIf="!isSearching">
          <div
            *ngFor="let user of filteredUsers; let i = index"
            (click)="toggleUser(user)"
            [class.selected-row]="isSelected(user.id)"
            [class.has-open-dropdown]="activeRoleDropdownUserId === user.id"
            class="user-item-row flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all"
          >
            <div class="flex items-center gap-3 min-w-0 flex-1 mr-2">
              <!-- Checkbox -->
              <div
                class="relative flex items-center justify-center flex-shrink-0"
              >
                <input
                  type="checkbox"
                  [checked]="isSelected(user.id)"
                  (click)="$event.stopPropagation()"
                  (change)="toggleUser(user)"
                  class="custom-checkbox"
                />
              </div>

              <!-- Initials Avatar / Profile Photo with dynamic color gradient fallback -->
              <div class="relative w-9 h-9 flex-shrink-0">
                <img
                  *ngIf="user.profilePhoto"
                  [src]="user.profilePhoto"
                  (error)="user.profilePhoto = null"
                  alt="Profile Photo"
                  class="w-9 h-9 rounded-full object-cover border border-gray-200 dark:border-[#1e2638]"
                />
                <div
                  *ngIf="!user.profilePhoto"
                  [ngClass]="getAvatarClass(user.name)"
                  class="avatar-badge flex items-center justify-center w-9 h-9 rounded-full text-xs font-semibold text-white shadow-sm"
                >
                  {{ user.initials || getInitials(user.name) }}
                </div>
              </div>

              <!-- Name & Email -->
              <div class="flex flex-col min-w-0">
                <span
                  class="text-sm font-semibold text-gray-800 dark:text-gray-100 leading-snug truncate"
                  >{{ user.name }}</span
                >
                <span
                  class="text-xs text-gray-500 dark:text-gray-400 leading-normal truncate"
                  >{{ user.email }}</span
                >
              </div>
            </div>

            <!-- Right side of row: Status Pill + Role Dropdown -->
            <div class="flex items-center gap-2 flex-shrink-0">
              <!-- Status Badge -->
              <span
                class="status-pill"
                [ngClass]="getStatusPillClass(user.status)"
              >
                {{ getDisplayStatus(user.status) }}
              </span>

              <!-- Role Dropdown for User -->
              <div class="relative row-role-dropdown-container">
                <button
                  type="button"
                  (click)="toggleRowRoleDropdown(user.id, $event, i)"
                  class="row-role-dropdown-btn"
                  [class.has-placeholder]="!user.role"
                >
                  <span class="truncate">{{ user.role || "Select role" }}</span>
                  <i
                    class="ki-outline ki-down text-[10px] text-gray-400 transition-transform duration-200 flex-shrink-0 ml-1"
                    [class.rotate-180]="activeRoleDropdownUserId === user.id"
                  ></i>
                </button>

                <!-- Dropdown Menu with Role Search -->
                <div
                  *ngIf="activeRoleDropdownUserId === user.id"
                  class="row-role-dropdown-panel"
                  [ngClass]="
                    isDropdownUpwards(user, i)
                      ? 'dropdown-upwards'
                      : 'dropdown-downwards'
                  "
                  (click)="$event.stopPropagation()"
                >
                  <!-- Sticky Search Bar inside Role Dropdown -->
                  <div class="role-dropdown-search-wrapper">
                    <div class="role-search-container">
                      <span class="role-search-icon-wrapper">
                        <svg
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </span>
                      <input
                        type="text"
                        [(ngModel)]="roleSearchQuery"
                        (click)="$event.stopPropagation()"
                        placeholder="Search role..."
                        class="role-search-input"
                      />
                      <button
                        *ngIf="roleSearchQuery"
                        type="button"
                        (click)="roleSearchQuery = ''; $event.stopPropagation()"
                        class="role-search-clear"
                        title="Clear search"
                      >
                        <svg
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Role Options List -->
                  <div
                    class="role-options-list max-h-48 overflow-y-auto custom-scrollbar"
                    (scroll)="onRoleDropdownScroll($event)"
                  >
                    <div
                      *ngFor="let role of filteredRoles"
                      (click)="selectRoleForUser(user, role, $event)"
                      [class.is-selected]="user.role === role"
                      class="row-role-option-item"
                    >
                      <span class="truncate">{{ role }}</span>
                      <i
                        *ngIf="user.role === role"
                        class="ki-outline ki-check text-xs text-blue-500"
                      ></i>
                    </div>
                    <div
                      *ngIf="filteredRoles.length === 0"
                      class="px-3 py-2 text-xs text-gray-400 text-center"
                    >
                      No roles found
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <!-- Scroll Loader -->
        <div
          *ngIf="isLoadingMore"
          class="py-4 text-center flex items-center justify-center gap-2.5 text-xs text-gray-400"
        >
          <div
            class="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
          ></div>
          <span>Loading more users...</span>
        </div>

        <!-- Rich No Data View -->
        <div
          *ngIf="!isSearching && filteredUsers.length === 0"
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <img
            src="./assets/media/icons/no-data.png"
            class="h-20 w-20 mb-3 opacity-90 select-none pointer-events-none"
            alt="No Data"
          />
          <span
            class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1"
          >
            {{ searchTerm ? "No results found" : "No users found" }}
          </span>
          <span
            class="text-xs text-gray-500 dark:text-gray-400 max-w-[280px] leading-relaxed"
          >
            {{
              searchTerm
                ? 'There is no user found matching "' +
                  searchTerm +
                  '". Try another name or email.'
                : "There is no data to show you right now."
            }}
          </span>
        </div>
      </div>
    </div>

    <!-- Right Section: Selected Users & Invitation Details -->
    <div class="right-panel flex flex-col p-4 justify-between">
      <!-- Selected Users Scrollable Area -->
      <div class="right-panel-content custom-scrollbar">
        <h3 class="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
          Selected Users ({{ selectedUserIds.size }})
        </h3>
        <div class="selected-chips flex flex-wrap gap-2 min-h-[40px]">
          <div
            *ngFor="let user of selectedUsers.slice(0, 10)"
            class="user-chip flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 dark:bg-[#111827] border border-blue-100 dark:border-[#1e293b] text-xs text-blue-600 dark:text-blue-400 font-semibold shadow-sm"
          >
            <span>{{ user.name }}</span>
            <span
              *ngIf="user.role"
              class="text-[10px] text-gray-700 font-normal"
              >({{ user.role }})</span
            >
            <button
              type="button"
              (click)="removeUser(user)"
              class="text-blue-500 dark:text-blue-400 hover:text-blue-800 dark:hover:text-white transition-colors bg-transparent border-0 cursor-pointer font-bold text-sm"
              title="Remove"
            >
              &times;
            </button>
          </div>
          <!-- More count badge -->
          <div
            *ngIf="selectedUsers.length > 10"
            class="user-chip flex items-center px-3.5 py-1.5 rounded-full bg-gray-100 dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] text-xs text-gray-700 dark:text-gray-300 font-semibold shadow-sm"
          >
            +{{ selectedUsers.length - 10 }} more
          </div>
          <div
            *ngIf="selectedUsers.length === 0"
            class="text-xs text-gray-400 dark:text-gray-500 py-1"
          >
            No users selected yet.
          </div>
        </div>
      </div>

      <!-- Bottom Invitation Info Notification -->
      <div
        *ngIf="selectedUserIds.size > 0"
        class="invite-notification-card"
        style="
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
          padding: 12px 14px !important;
          border-radius: 10px !important;
          width: 100% !important;
          box-sizing: border-box !important;
          margin-top: auto !important;
          flex-shrink: 0 !important;
        "
      >
        <div
          class="invite-info-icon-badge"
          style="
            width: 28px !important;
            height: 28px !important;
            min-width: 28px !important;
            min-height: 28px !important;
            border-radius: 50% !important;
            background-color: #1b84ff !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            flex-shrink: 0 !important;
          "
        >
          <svg
            viewBox="0 0 16 16"
            width="15"
            height="15"
            fill="#ffffff"
            style="width: 15px; height: 15px; display: block;"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="3.75" r="1.5" fill="#ffffff" />
            <rect
              x="6.75"
              y="6.75"
              width="2.5"
              height="6.5"
              rx="1.25"
              fill="#ffffff"
            />
          </svg>
        </div>
        <div
          class="invite-notification-body"
          style="
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            min-width: 0 !important;
            flex: 1 !important;
          "
        >
          <p
            class="invite-notification-title"
            style="
              margin: 0 !important;
              padding: 0 !important;
              font-size: 13px !important;
              font-weight: 600 !important;
              line-height: 1.35 !important;
              letter-spacing: -0.01em !important;
            "
          >
            {{ selectedUserIds.size }}
            {{ selectedUserIds.size === 1 ? 'user' : 'users' }} will be invited
            with their respective roles.
          </p>
          <p
            class="invite-notification-subtitle"
            style="
              margin: 2px 0 0 0 !important;
              padding: 0 !important;
              font-size: 11.5px !important;
              font-weight: 400 !important;
              line-height: 1.35 !important;
            "
          >
            They will receive an invitation email to set up their account.
          </p>
        </div>
      </div>
    </div>
  </div>
</div>
